<template>
  <v-btn tile large class="primary" @click="calculatePaymentPlan" :loading="loading" :disabled="disabled">
    {{ $t('cppb-text-1') }}
    <v-dialog v-model="showDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <v-card class="dialog-card">
        <v-toolbar flat light color="white" v-if="dialogSettings.fullscreen">
          <v-toolbar-title>{{ $t('cppb-text-2') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click.native="closeDialog">
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-toolbar>
        <v-divider></v-divider>

        <v-stepper v-model="progress">
          <v-stepper-items>
            <v-stepper-content class="pt-0" step="1">
              <!-- Standard Payment Plan Section (Non-BanditRating 2) -->
              <v-container v-if="!showAdditionalPlans" class="pa-0 grow" fluid>
                <v-row no-gutters>
                  <v-card-text>
                    <v-col cols="12" class="text-right" v-if="!dialogSettings.fullscreen">
                      <v-btn color="color-grey" depressed right text small @click="closeDialog">
                        <v-icon class="mr-3">fa-times</v-icon>
                        <span>{{ $t('close') }}</span>
                      </v-btn>
                    </v-col>
                    <v-col cols="12">
                      <v-row>
                        <v-col :class="{ 'text-center': $vuetify.breakpoint.mdAndUp }">
                          <h3 class="mt-0 text-h3" v-if="!dialogSettings.fullscreen">{{ $t('cppb-text-2') }}</h3>
                          <span v-if="installments.length > 0">
                            {{ $t('cppb-text-3') }}
                          </span>
                          <div><b>{{ $t('cppb-text-4') }}</b></div>
                          <div><b>{{ $t('paymentPlanInterestNotion') }}</b></div>
                        </v-col>
                      </v-row>
                      <!-- Main payment plan -->
                      <v-row :class="{'justify-center': $vuetify.breakpoint.mdAndUp }" v-if="installments.length > 0">
                        <div class="pa-3">
                          <p class="ok-title">{{ $t('cppb-text-5') }}</p>
                          <span class="ok-subtitle font-weight-bold mr-3">{{ installments[0].amount | currency }}</span>
                          <span v-if="hasBanditRating3" class="ok-subtitle warning--text">{{ $t('cppb-due-today') }}</span>
                          <span v-else class="ok-subtitle">{{ installments[0].dueDate | formatDate }}</span>
                        </div>
                        <div class="pa-3">
                          <p class="ok-title">{{ $t('cppb-text-6') }}</p>
                          <p class="ok-subtitle">{{ installments.length }} {{ $t('kpl') }}</p>
                        </div>
                        <div class="pa-3">
                          <p class="ok-title">{{ $t('cppb-text-7') }}</p>
                          <p class="ok-subtitle">{{ calculatedPaymentPlan.paymentPlan.price | currency }}</p>
                        </div>
                      </v-row>
                    </v-col>
                  </v-card-text>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-divider></v-divider>
                    <v-expansion-panels flat>
                      <v-expansion-panel v-if="installments.length > 0">
                        <v-expansion-panel-header class="pp-expansion-header py-6 text-center" expand-icon="fas fa-chevron-circle-down">
                          <span class="ok-title mb-0">{{ $t('cppb-text-10') }}</span>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-data-table :headers="headers"
                                        :items="installments"
                                        hide-default-footer
                                        :items-per-page="-1"
                                        :mobile-breakpoint="0">
                            <template #item.sequence="{ item }">
                              <v-badge color="white">
                                <span slot="badge" class="black--text">{{ item.sequence }}</span>
                              </v-badge>
                            </template>
                            <template #item.duedate="{ item }">
                              {{ item.dueDate | formatDate }}
                            </template>
                            <template #item.amount="{ item }">
                              {{ item.amount | currency }}
                            </template>
                            <template #item.capital="{ item }">
                              {{ item.capital | currency }}
                            </template>
                            <template #item.costs="{ item }">
                              {{ item.costs | currency }}
                            </template>
                            <template #item.interest="{ item }">
                              {{ item.interest | currency }}
                            </template>
                          </v-data-table>
                          <v-row no-gutters justify="center">
                            <div class="pa-3">
                              <span class="ok-title small mr-2">{{ $t('capital') }}:</span>
                              <span class="text-caption">{{ calculatedPaymentPlan.totalCapital | currency }}</span>
                            </div>
                            <div class="pa-3">
                              <span class="ok-title small mr-2">{{ $t('interests') }}:</span>
                              <span class="text-caption">{{ calculatedPaymentPlan.totalInterest | currency }}</span>
                            </div>
                            <div class="pa-3">
                              <span class="ok-title small mr-2">{{ $t('cppb-text-9') }}:</span>
                              <span class="text-caption">{{ calculatedPaymentPlan.totalCost | currency }}</span>
                            </div>
                          </v-row>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                    <v-divider></v-divider>
                  </v-col>
                </v-row>
              </v-container>

              <!-- BanditRating 2 Payment Plan Selection Section -->
              <v-container v-if="showAdditionalPlans" class="additional-plans-container" fluid>
                <v-row no-gutters>
                  <v-card-text>
                    <v-col cols="12" class="text-right" v-if="!dialogSettings.fullscreen">
                      <v-btn color="color-grey" depressed right text small @click="closeDialog">
                        <v-icon class="mr-3">fa-times</v-icon>
                        <span>{{ $t('close') }}</span>
                      </v-btn>
                    </v-col>
                    <v-col cols="12">
                      <v-row>
                        <v-col :class="{ 'text-center': $vuetify.breakpoint.mdAndUp }">
                          <h3 class="mt-0 text-h3" v-if="!dialogSettings.fullscreen">{{ $t('cppb-text-2-bandit-2') }}</h3>
                          <span v-if="installments.length > 0">
                            {{ $t('cppb-text-3-bandit-2') }}
                          </span>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-card-text>
                </v-row>

                <!-- Loading additional plans -->
                <v-row v-if="loadingAdditionalPlans" justify="center">
                  <v-col cols="12" class="text-center">
                    <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
                    <p class="text-subtitle-1">{{ $t('cppb-loading-additional') }}</p>
                  </v-col>
                </v-row>

                <!-- Payment plan selection with radio buttons -->
                <template v-if="!loadingAdditionalPlans && allPaymentPlans.length > 0">
                  <v-row justify="center">
                    <v-col cols="12" md="8">
                      <span class="ok-title big">{{ $t('cppb-select-plan') }}</span>
                      <v-radio-group v-model="selectedPaymentPlan" column class="additional-plans-radio-group">
                        <v-radio v-for="(plan, index) in allPaymentPlans"
                                 :key="`plan-${index}`"
                                 :value="plan"
                                 color="primary"
                                 class="pplan-radio-option"
                                 on-icon="far fa-check-square"
                                 off-icon="far fa-square">
                          <template v-slot:label>
                            <v-row no-gutters align="center">
                              <div class="pa-3">
                                <p class="text-h6 font-weight-bold mb-0 mt-0">{{ plan.paymentPlan.installments[0].amount | currency }} {{ $t('cppb-per-month') }} </p>
                                <span>{{ plan.paymentPlan.installments.length }} {{ $t('cppb-installments') }}, {{ sumInstallments(plan.paymentPlan.installments, 'amount') | currency }} {{ $t('cppb-int-total') }}</span>
                              </div>
                              <div class="px-3">
                                <v-chip v-if="!plan.isOriginal" label class="ma-2 color-white--text" color="color-new-link-blue">
                                  <v-icon left x-small>
                                    fas fa-coins
                                  </v-icon>
                                  {{ $t('cppb-you-save') }} {{ plan.saveVersusOriginal | currency }}
                                </v-chip>
                              </div>
                            </v-row>
                          </template>
                        </v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </template>
              </v-container>

              <!-- Phone Number Section -->
              <v-container class="pa-0 grow" fluid>
                <v-row no-gutters>
                  <v-card-text>
                    <v-row justify="center" align="center" v-if="installments.length > 0 && !disablePhoneNumberAdd">
                      <v-col cols="12" sm="8">
                        <p class="text-h8 text-center mt-0">
                          {{ $t('cppb-text-11') }}
                        </p>
                      </v-col>
                      <v-col cols="12" sm="8">
                        <span class="text-subtitle-1 mb-0" :class="{ 'mr-2': $vuetify.breakpoint.mdAndUp }" v-if="phoneNumbers.length > 0">{{ $t('cppb-text-12') }}</span>
                        <br v-if="$vuetify.breakpoint.smAndDown" />
                        <span class="text-subtitle-1 mb-0 font-weight-bold" :class="{ 'mr-6': $vuetify.breakpoint.mdAndUp }" v-if="phoneNumbers.length > 0">{{ phoneNumbers[0].number }}</span>
                        <br v-if="$vuetify.breakpoint.smAndDown" />
                        <v-btn text class="color-new-title-grey--text px-0" @click="showPhoneForm = true" v-show="!showPhoneForm">
                          <v-icon class="mr-2">far fa-edit</v-icon>
                          {{ phoneNumbers.length > 0 ? $t('cppb-text-14') : $t('cppb-text-13') }}
                        </v-btn>
                      </v-col>
                      <v-col cols="12" sm="8" v-show="showPhoneForm">
                        <v-form @submit.prevent="addPhoneNumber()">
                          <v-row no-gutters align="center">
                            <v-col cols="8" sm="5" class="pr-3">
                              <v-text-field :label="$t('phone')"
                                            v-model="phoneNumber"
                                            @blur="$v.phoneNumber.$touch()"
                                            @change="$v.phoneNumber.$touch()"
                                            prefix="+358"
                                            class="long-prefix"
                                            placeholder=" "
                                            persistent-hint
                                            :error-messages="phoneNumberError">
                              </v-text-field>
                            </v-col>
                            <v-col cols="3">
                              <v-btn small tile color="success" @click="addPhoneNumber()" :disabled="$v.phoneNumber.$error || !$v.phoneNumber.$dirty || phoneNumber.length === 0">{{ $t('add') }}</v-btn>
                            </v-col>
                          </v-row>
                        </v-form>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-row>
              </v-container>

              <!-- Action Buttons -->
              <v-card-actions class="justify-center pa-4">
                <div class="pa-3">
                  <v-btn tile dark class="primary" v-if="installments.length > 0" @click="createPaymentPlan" :loading="creating">{{ $t('cppb-text-8') }}</v-btn>
                </div>
                <div class="pa-3">
                  <v-btn tile outlined dark color="color-new-link-blue" @click="closeDialog">{{ $t('cancel') }}</v-btn>
                </div>
              </v-card-actions>

            </v-stepper-content>

            <v-stepper-content class="pt-0" step="2">
              <v-container v-if="!showAdditionalPlans" class="pa-0 grow" fluid>
                <v-row no-gutters>
                  <v-card-text>
                    <v-col cols="12">
                      <div class="payment-form">
                        <shared-payment-forms :paymentData="paymentData" :cancellable="false" />
                      </div>
                    </v-col>
                  </v-card-text>
                  </v-row>
                </v-container>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-dialog>
  </v-btn>
</template>

<script>
  import { apiResource } from '@/util/resources'
  import { numeric, minLength, maxLength } from 'vuelidate/lib/validators'

  export default {
    props: ['debts', 'amount', 'desiredDueDate'],
    data: function () {
      return {
        loading: false,
        creating: false,
        showDialog: false,
        calculatedPaymentPlan: null,
        additionalPaymentPlans: [],
        loadingAdditionalPlans: false,
        selectedPaymentPlan: null, // Selected plan from radio group
        phoneNumber: '',
        showPhoneForm: false,
        progress: 1,
        paymentData: []
      }
    },
    computed: {
      debtor() {
        return this.$store.getters.debtor
      },
      phoneNumbers() {
        return this.debtor.contactInformation.phones
      },
      dialogSettings() {
        return {
          fullscreen: this.$vuetify.breakpoint.smAndDown,
          scrollable: false, // this.$vuetify.breakpoint.smAndDown,
          hideoverlay: this.$vuetify.breakpoint.smAndDown,
          transition: this.$vuetify.breakpoint.smAndDown ? 'dialog-bottom-transition' : 'dialog-transition',
          width: this.$vuetify.breakpoint.smAndDown ? 'auto' : '1000px',
          maxwidth: this.$vuetify.breakpoint.smAndDown ? 'none' : '90%'
        }
      },
      paymentPlanRequest() {
        var debtNrs = this.debts.map(function (s) { return s.nr })
        return {
          debtNrs: debtNrs,
          type: 0,
          amount: this.selectedPaymentPlan ? this.selectedPaymentPlan.originalAmount || this.amount : this.amount,
          date: parseInt(this.desiredDueDate, 10)
        }
      },
      installments() {
        return this.calculatedPaymentPlan !== null ? this.calculatedPaymentPlan.paymentPlan.installments : []
      },
      disabled() {
        if (this.amount === null || this.debts.length === 0) {
          return true
        }

        var minAmount = 0
        var maxAmount = 0

        if (this.installments.length && this.installments[1].amount > this.amount) {
          minAmount = this.installments[1].amount
        }

        this.debts.forEach((debt) => {
          maxAmount += debt.debtSummary.left
        })

        if (this.amount >= minAmount && this.amount <= maxAmount) {
          return false
        } else {
          return true
        }
      },
      headers() {
        let desktopHeaders = [
          { text: this.$t('installment'), value: 'sequence', sortable: false, class: 'pp-table-header' },
          { text: this.$t('dueDate'), value: 'duedate', sortable: false, class: 'pp-table-header' },
          { text: this.$t('installmentSequence'), value: 'amount', sortable: false, class: 'pp-table-header' },
          { text: this.$t('capital'), value: 'capital', sortable: false, class: 'pp-table-header' },
          { text: this.$t('costs'), value: 'costs', sortable: false, class: 'pp-table-header' },
          { text: this.$t('interests'), value: 'interest', sortable: false, class: 'pp-table-header' }
        ]

        let mobileHeaders = [
          { text: this.$t('installment'), value: 'sequence', sortable: false, class: 'pp-table-header' },
          { text: this.$t('dueDate'), value: 'duedate', sortable: false, class: 'pp-table-header' },
          { text: this.$t('installmentSequence'), value: 'amount', sortable: false, class: 'pp-table-header' }
        ]

        return this.$vuetify.breakpoint.smAndDown ? mobileHeaders : desktopHeaders
      },
      disablePhoneNumberAdd() {
        return this.$store.getters.hidePersonalInfo
      },
      phoneNumberError() {
        const errors = []
        if (!this.$v.phoneNumber.$dirty || this.phoneNumber === '') return errors
        !this.$v.phoneNumber.numeric && errors.push(this.$t('validation.phone.numeric'))
        !this.$v.phoneNumber.minLength && errors.push(this.$t('validation.phone.length'))
        !this.$v.phoneNumber.maxLength && errors.push(this.$t('validation.phone.length'))
        return errors
      },
      hasBanditRating2() {
        return this.debts.some(debt => debt.banditRating === 2)
      },
      hasBanditRating3() {
        return this.debts.some(debt => debt.banditRating === 3)
      },
      showAdditionalPlans() {
        return this.hasBanditRating2 && this.isAmountAboveMinimum
      },
      isAmountAboveMinimum() {
        if (!this.calculatedPaymentPlan || !this.installments.length) return false
        return this.amount >= this.installments[1].amount
      },
      validAdditionalPaymentPlans() {
        return this.additionalPaymentPlans.filter(plan =>
          plan &&
          plan.paymentPlan &&
          plan.paymentPlan.installments &&
          Array.isArray(plan.paymentPlan.installments) &&
          plan.paymentPlan.installments.length > 0
        )
      },
      allPaymentPlans() {
        if (!this.calculatedPaymentPlan) return []

        const plans = []

        // Add original plan first
        plans.push({
          ...this.calculatedPaymentPlan,
          isOriginal: true,
          percentageIncrease: 0,
          originalAmount: this.amount
        })

        // Add additional plans
        this.validAdditionalPaymentPlans.forEach(plan => {
          plans.push(plan)
        })

        return plans
      }
    },
    methods: {
      closeDialog() {
        this.loading = false
        this.creating = false
        this.showDialog = false
        this.calculatedPaymentPlan = null
        this.additionalPaymentPlans = []
        this.loadingAdditionalPlans = false
        this.selectedPaymentPlan = null
        this.phoneNumber = ''
        this.showPhoneForm = false
      },
      calculatePaymentPlan: function (event) {
        this.loading = true
        this.calculatedPaymentPlan = null
        this.additionalPaymentPlans = []
        this.loadingAdditionalPlans = false
        this.selectedPaymentPlan = null

        let payload = {
          DebtNrs: this.paymentPlanRequest.debtNrs,
          Type: this.paymentPlanRequest.type,
          Amount: this.paymentPlanRequest.amount,
          DateSelection: this.paymentPlanRequest.date,
          Create: false
        }

        apiResource.post('api/paymentPlan/calculate', payload).then((response) => {
          this.loading = false
          if (response.data.status === 0) {
            this.calculatedPaymentPlan = response.data.responseObject

            if (this.installments.length && this.installments[1].amount <= this.paymentPlanRequest.amount) {
              this.showDialog = true

              // For BanditRating 2, set the original plan as selected and calculate additional plans
              if (this.showAdditionalPlans) {
                // Pre-select the original plan
                this.selectedPaymentPlan = {
                  ...this.calculatedPaymentPlan,
                  isOriginal: true,
                  percentageIncrease: 0,
                  originalAmount: this.amount,
                  saveVersusOriginal: 0
                }
                this.calculateAdditionalPlans()
              }

              if (payload.DebtNrs.length > 1) {
                this.$matomo && this.$matomo.trackEvent("Management", "Create Payment plan", "Create CPP Start")
              } else {
                this.$matomo && this.$matomo.trackEvent("Management", "Create Payment plan", "Create PP Start")
              }
            } else {
              this.$emit('minAmount', this.installments.length ? this.installments[1].amount : null)
            }
          } else {
            this.$store.commit('setMessage', { type: 'error', text: 'Virhe maksusuunnitelman luonnissa.' })
          }
        }).catch((errorResponse) => {
          console.log(errorResponse)
          this.$store.commit('setMessage', { type: 'error', text: 'Virhe.' })
          this.loading = false
          this.calculatedPaymentPlan = null
          this.additionalPaymentPlans = []
          this.loadingAdditionalPlans = false
        })
      },
      async calculateAdditionalPlans() {
        this.loadingAdditionalPlans = true
        this.additionalPaymentPlans = []

        const increases = [20, 30]
        const calculatedAmounts = []

        // Calculate the maximum allowed amount (sum of all debt.debtSummary.left)
        const maxAllowedAmount = this.debts.reduce((sum, debt) => sum + debt.debtSummary.left, 0)

        increases.forEach(percentage => {
          // Calculate the increased amount
          const baseIncreasedAmount = this.amount * (1 + percentage / 100)

          // Round up to the nearest ten
          let roundedAmount = Math.ceil(baseIncreasedAmount / 10) * 10

          // Check if the rounded amount exceeds the maximum allowed amount
          if (roundedAmount > maxAllowedAmount) {
            console.warn(`Increased amount ${roundedAmount} for ${percentage}% exceeds maximum allowed ${maxAllowedAmount}. Skipping this calculation.`)
            return // Skip this percentage if it exceeds the limit
          }

          calculatedAmounts.push({
            percentage,
            amount: roundedAmount
          })
        })

        // Only proceed if we have valid calculated amounts
        if (calculatedAmounts.length === 0) {
          console.warn('No valid additional payment plans could be calculated within the debt limits.')
          this.loadingAdditionalPlans = false
          return
        }

        // Check if 20% and 30% result in the same rounded amount
        if (calculatedAmounts.length === 2 && calculatedAmounts[0].amount === calculatedAmounts[1].amount) {
          // Add 10 to the 30% calculation (latter value)
          const newAmount = calculatedAmounts[1].amount + 10

          // Check if the new amount still doesn't exceed the limit
          if (newAmount <= maxAllowedAmount) {
            calculatedAmounts[1].amount = newAmount
          } else {
            // If adding 10 would exceed the limit, remove the 30% option
            console.warn(`Adding 10 to 30% calculation would exceed debt limit. Removing 30% option.`)
            calculatedAmounts.pop() // Remove the 30% calculation
          }
        }

        // Create promises for all API calls
        const apiPromises = calculatedAmounts.map(({ percentage, amount }) => {
          let payload = {
            DebtNrs: this.paymentPlanRequest.debtNrs,
            Type: this.paymentPlanRequest.type,
            Amount: amount,
            DateSelection: this.paymentPlanRequest.date,
            Create: false
          }

          return apiResource.post('api/paymentPlan/calculate', payload)
            .then((response) => {
              if (response.data.status === 0 && response.data.responseObject) {
                const responseObject = response.data.responseObject

                // Validate that the response has the expected structure
                if (responseObject.paymentPlan &&
                  responseObject.paymentPlan.installments &&
                  Array.isArray(responseObject.paymentPlan.installments) &&
                  responseObject.paymentPlan.installments.length > 0) {

                  return {
                    ...responseObject,
                    isOriginal: false,
                    percentageIncrease: percentage,
                    originalAmount: amount,
                    baseAmount: this.amount * (1 + percentage / 100),
                    roundedAmount: amount,
                    maxAllowedAmount: maxAllowedAmount,
                    saveVersusOriginal: this.sumInstallments(this.calculatedPaymentPlan.paymentPlan.installments, 'amount') - this.sumInstallments(responseObject.paymentPlan.installments, 'amount')
                  }
                } else {
                  console.warn('Invalid response structure for additional payment plan:', responseObject)
                  return null
                }
              } else {
                console.warn('API returned error status for additional payment plan calculation:', response.data)
                return null
              }
            })
            .catch((errorResponse) => {
              console.log('Error calculating additional payment plan:', errorResponse)
              return null
            })
        })

        // Wait for all API calls to complete
        try {
          const results = await Promise.all(apiPromises)

          // Filter out null results and add valid plans
          const validPlans = results.filter(plan => plan !== null)

          // Sort by percentage increase
          validPlans.sort((a, b) => a.percentageIncrease - b.percentageIncrease)

          this.additionalPaymentPlans = validPlans

        } catch (error) {
          console.error('Error loading additional payment plans:', error)
        } finally {
          this.loadingAdditionalPlans = false
        }
      },
      addPhoneNumber() {
        let payload = { ContactType: 'phone', Text: this.phoneNumber, Allow: true }
        this.$store.dispatch('addDebtorContact', payload)
        this.phoneNumber = ''
      },
      createPaymentPlan: function (event) {
        this.creating = true

        let payload = {
          DebtNrs: this.paymentPlanRequest.debtNrs,
          Type: this.paymentPlanRequest.type,
          Amount: this.paymentPlanRequest.amount,
          DateSelection: this.paymentPlanRequest.date,
          Create: true
        }

        apiResource.post('api/paymentPlan/create', payload).then((response) => {
          this.creating = false
          if (response.data.status === 0) {
            if (response.data.responseObject === true) {
              if (this.hasBanditRating3) {
                this.progress = 2
                this.setPaymentData()
              } else {
                let p = {
                  type: 'paymentplan'
                }
                if (payload.DebtNrs.length > 1) {
                  p.redirectToLatestPaymentPlan = true
                }
                this.$store.dispatch('initialize', p)
                this.$store.commit('setMessage', { type: 'success', text: this.$t('paymentPlanResults.0') })
                this.showDialog = false

                if (payload.DebtNrs.length > 1) {
                  this.$matomo && this.$matomo.trackEvent("Management", "Create Payment plan", "Create CPP Completed", this.paymentPlanRequest.amount)
                } else {
                  this.$matomo && this.$matomo.trackEvent("Management", "Create Payment plan", "Create PP Completed", this.paymentPlanRequest.amount)
                }
              }
            } else {
              this.$store.commit('setMessage', { type: 'error', text: 'Virhe maksusuunnitelman luonnissa.' })

              if (payload.DebtNrs.length > 1) {
                this.$matomo && this.$matomo.trackEvent("Management", "Create Payment plan", "Create CPP Failed")
              } else {
                this.$matomo && this.$matomo.trackEvent("Management", "Create Payment plan", "Create PP Failed")
              }
            }
          } else {
            this.$store.commit('setMessage', { type: 'error', text: 'Virhe maksusuunnitelman luonnissa.' })

            if (payload.DebtNrs.length > 1) {
              this.$matomo && this.$matomo.trackEvent("Management", "Create Payment plan", "Create CPP Failed")
            } else {
              this.$matomo && this.$matomo.trackEvent("Management", "Create Payment plan", "Create PP Failed")
            }
          }
        }).catch((errorResponse) => {
          console.log(errorResponse)
          this.$store.commit('setMessage', { type: 'error', text: 'Virhe.' })
          this.creating = false
        })
      },
      sumInstallments(installments, property) {
        if (!installments || !Array.isArray(installments)) return 0
        return installments.reduce((total, item) => total + (item[property] || 0), 0)
      },
      setPaymentData() {
        if (this.calculatedPaymentPlan !== null) {
          this.paymentData = {
            debtNr: this.debts[0].nr,
            description: null,
            amount: this.calculatedPaymentPlan.paymentPlan.installments[0].amount,
            paymentType: 'installment'
          } 
        } else {
          this.paymentData = null
        }
      }
    },
    watch: {
      showDialog(val, oldVal) {
        if (!val && oldVal) {
          this.loading = false
          this.creating = false
          this.calculatedPaymentPlan = null
          this.additionalPaymentPlans = []
          this.loadingAdditionalPlans = false
          this.selectedPaymentPlan = null
        }
      }
    },
    validations: {
      phoneNumber: {
        numeric,
        minLength: minLength(1),
        maxLength: maxLength(12)
      }
    }
  }
</script>

<i18n>
  {
  "fi": {
  "cppb-text-1": "Jatka vahvistamaan",
  "cppb-text-2": "Vahvista maksusuunnitelma",
  "cppb-text-2-bandit-2": "Säästä maksamalla nopeammin",
  "cppb-text-3": "Alla näet valitsemasi maksuohjelman tiedot. Muistathan, että maksusuunnitelman tekeminen on sitovaa.",
  "cppb-text-3-bandit-2": "Jos mahdollista, suosittelemme että kasvatat kuukausierää. Näin voit säästää rahaa kuluissa ja koroissa:",
  "cppb-text-4": "Hyväksymisen jälkeen voit tarkistaa maksusuunnitelman ajantasaiset tiedot Oma B2 Impact -palvelussa.",
  "cppb-text-5": "Ensimmäinen erä",
  "cppb-text-6": "Eriä",
  "cppb-text-7": "Maksusuunnitelman hinta",
  "cppb-text-8": "Hyväksy & vahvista",
  "cppb-text-9": "Kulut yhteensä",
  "cppb-text-10": "Näytä suunnitelman tarkemmat tiedot",
  "cppb-text-11": "Muistutamme eräpäivästä tekstiviestillä.",
  "cppb-text-12": "Toimitamme viestin numeroon",
  "cppb-text-13": "Lisää puhelinnumero",
  "cppb-text-14": "Vaihda puhelinnumero",
  "cppb-loading-additional": "Lasketaan vaihtoehtoisia maksusuunnitelmia...",
  "cppb-select-plan": "Valitse vaihtoehto",
  "cppb-per-month": "kuukaudessa",
  "cppb-installments": "erää",
  "cppb-int-total": "yhteensä",
  "cppb-you-save": "Voit säästää",
  "cppb-due-today": "Maksettava tänään"
  },
  "se": {
  "cppb-text-1": "Fortsätt",
  "cppb-text-2": "Bekräfta betalningsplanen",
  "cppb-text-2-bandit-2": "Spara genom att betala snabbare",
  "cppb-text-3": "Nedan ser du information om den betalningsplan du har valt. Kom ihåg att det är bindande att göra en betalningsplan.",
  "cppb-text-3-bandit-2": "Om möjligt rekommenderar vi att du ökar månadsbetalningen för att spara pengar på räntor och avgifter:",
  "cppb-text-4": "Efter godkännandet kan du kontrollera de aktuella detaljerna för din betalningsplan i tjänsten Oma B2 Impact.",
  "cppb-text-5": "Första raten",
  "cppb-text-6": "Rater",
  "cppb-text-7": "Kostnad för betalningsplanen",
  "cppb-text-8": "Godkänn & bekräfta",
  "cppb-text-9": "Kostnader totalt",
  "cppb-text-10": "Visa detaljerad information",
  "cppb-text-11": "Vi skickar ett SMS på förfallodagen till ditt telefonnummer",
  "cppb-text-12": "Vi skickar ett SMS på förfallodagen till ditt telefonnummer, var vänlig och sätt till ditt telefonnummer:",
  "cppb-text-13": "Sätt till telefonnummer",
  "cppb-text-14": "Vaihda puhelinnumero",
  "cppb-loading-additional": "Beräknar alternativa betalningsplaner...",
  "cppb-select-plan": "Välj alternativ",
  "cppb-per-month": "per månad",
  "cppb-installments": "delbetalningar",
  "cppb-int-total": "totalt",
  "cppb-you-save": "Du kan spara",
  "cppb-due-today": "Förfaller idag"
  },
  "en": {
  "cppb-text-1": "Continue",
  "cppb-text-2": "Confirm the payment plan",
  "cppb-text-2-bandit-2": "Save by paying up faster",
  "cppb-text-3": "Below you can see the details of your selected payment plan. Please note that creating a payment plan is binding.",
  "cppb-text-3-bandit-2": "If possible, we recommend you increase the monthly payment in order to save money in interests and fees:",
  "cppb-text-4": "After approval, you can check the up-to-date details of your payment plan in the Oma B2 Impact service.",
  "cppb-text-5": "First installment",
  "cppb-text-6": "Installments",
  "cppb-text-7": "Cost of the payment plan",
  "cppb-text-8": "Confirm",
  "cppb-text-9": "Total cost",
  "cppb-text-10": "Show detailed information",
  "cppb-text-11": "We will remind you about payment",
  "cppb-text-12": "We will send a SMS-message to your phone when next payment is due. Message will be sent to",
  "cppb-text-13": "Add phone number",
  "cppb-text-14": "Change phone number",
  "cppb-loading-additional": "Calculating alternative payment plans...",
  "cppb-select-plan": "Choose option",
  "cppb-per-month": "per month",
  "cppb-installments": "installments",
  "cppb-int-total": "in total",
  "cppb-you-save": "You can save",
  "cppb-due-today": "Due today"
  }
  }
</i18n>

<style>
  .theme--light .v-table {
    background-color: inherit;
  }
</style>
